import Swal from "sweetalert2";
import ApiService, { HttpMethod } from "../../../../commons/api/api_service";
import BaseResponse from "../../../../commons/api/base_response";
import { Endpoints } from "../../../../commons/api/endpoints";
import { ProviderVM } from "../../../../commons/base/providers/view_model";
import {
    ShowErrorViewModel,
    ShowSnackbarModel,
} from "../../../../commons/scaffold/scaffold";
import {
    AdjustmentTransferModel,
    ItemOptions,
    LocationModel,
    SubmitAdjustmentTransferModel,
    SubmitTransferAdjustmentResponse,
    TransferDetailResponse,
    TransferResponse,
} from "../models/adjustment_forms_transfer_model";

export default class AdjustmentFormsTransferViewModel extends ProviderVM {
    // UI States
    isLoading: boolean = false;
    errorMessage: string = "";
    showSnackbar?: ShowSnackbarModel = undefined;
    showErrorView?: ShowErrorViewModel = undefined;
    isShowModal: boolean = false;

    // Data States
    locationOptions: LocationModel[] = [];
    filterSenderLocationId?: number;
    itemOptions: ItemOptions[] = [];
    selectedItemId?: number;
    addedItemIds: number[] = [];
    transferList: TransferResponse[] = [];
    filteredTransferList: TransferResponse[] = [];

    searchQuery: string = "";
    formId?: number;
    formCode?: string;
    formNewSenderLocationId?: number;
    formNewReceiverLocationId?: number;
    formPhoneNumber?: string;
    transferDataMap: Map<string, AdjustmentTransferModel> = new Map<
        string,
        AdjustmentTransferModel
    >();

    constructor() {
        super();
        this.setup();
    }

    setup() {
        this.fetchTransferList({
            senderLocationId: this.filterSenderLocationId,
        });
        this.fetchLocationOptions();
        this.fetchItemOptions();
    }

    // Fetch Data
    fetchLocationOptions() {
        this.setIsLoading(true);
        ApiService.fetch(Endpoints.adjustmentFormGetLocationOptions, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<LocationModel[]>) => {
                this.setIsLoading(false);
                if (response.code === 200) {
                    this.setLocationOptions(response.data as LocationModel[]);
                } else {
                    console.log(response);
                    this.setErrorMessage(response.message);
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                }
            },
            onFailure: (error) => {
                this.setIsLoading(false);
                console.log(error);
                this.setErrorMessage(
                    "Failed fetching data, please refresh or try again"
                );
                this.setShowErrorView({
                    action: () => {
                        this.reset();
                        this.setShowErrorView(undefined);
                        this.setErrorMessage("");
                        this.setup();
                    },
                    message: this.errorMessage,
                });
            },
        });
    }

    fetchItemOptions() {
        this.setIsLoading(true);
        ApiService.fetch(Endpoints.adjustmentFormGetItemOptions, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<ItemOptions[]>) => {
                this.setIsLoading(false);
                if (response.code === 200) {
                    const itemOptions = response.data as ItemOptions[];
                    this.setItemOptions(itemOptions);
                } else {
                    console.log(response);
                    this.setErrorMessage(response.message);
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setup();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                        },
                        message: this.errorMessage,
                    });
                }
            },
            onFailure: (error) => {
                console.log(error);
                this.setIsLoading(false);
                this.setErrorMessage(
                    "Failed fetching data, please refresh or try again"
                );
                this.setShowErrorView({
                    action: () => {
                        this.reset();
                        this.setup();
                        this.setShowErrorView(undefined);
                        this.setErrorMessage("");
                    },
                    message: this.errorMessage,
                });
            },
        });
    }

    fetchTransferList(args: {
        senderLocationId?: number;
        receiverLocationId?: number;
    }) {
        this.setIsLoading(true);

        ApiService.fetchWithQueryParams(
            Endpoints.adjustmentFormGetTransferList,
            {
                httpMethod: HttpMethod.get,
                queryParameters: {
                    ...(args.senderLocationId && {
                        fromLocationId: args.senderLocationId,
                    }),
                },
                onSuccess: (response: BaseResponse<TransferResponse[]>) => {
                    this.setIsLoading(false);
                    if (response.code === 200) {
                        const transferList =
                            response.data as TransferResponse[];
                        this.setTransferList(transferList);
                        this.setFilteredTransferList(transferList);
                    }
                },
                onFailure: (error) => {
                    this.setIsLoading(false);
                    console.log(error);
                    this.setErrorMessage(
                        "Failed fetching data, please refresh or try again"
                    );
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                },
            }
        );
    }

    fetchTransferDetail(transferId: number) {
        this.setIsLoading(true);
        ApiService.fetch(
            `${Endpoints.adjustmentFormGetTransferDetail}/${transferId}` as Endpoints,
            {
                httpMethod: HttpMethod.get,
                onSuccess: (response: BaseResponse<TransferDetailResponse>) => {
                    this.setIsLoading(false);
                    if (response.code === 200) {
                        const data = response.data as TransferDetailResponse;

                        this.setFormId(data.formId);
                        this.setFormCode(data.transferCode);
                        this.setFormNewSenderLocationId(data.fromLocationId);
                        this.setFormNewReceiverLocationId(data.toLocationId);
                        this.setFormPhoneNumber(data.phoneNo);

                        const map = new Map<string, AdjustmentTransferModel>();
                        const addedItemIds: number[] = [];
                        data.items.forEach((item) => {
                            const uniqueId = `${item.rowId}-${item.masterItemId}`;
                            map.set(uniqueId, {
                                rowId: item.rowId,
                                itemId: item.masterItemId,
                                uniqueId: uniqueId,
                                isFromRevision: item.isFromRevision,
                                isNew: false,
                                itemName: item.masterItemName,
                                bigQty: item.qtyBigUnit,
                                smallQty: item.qtySmallUnit,
                                bigUnit: item.bigUnit,
                                smallUnit: item.smallUnit,
                            });
                            addedItemIds.push(item.masterItemId);
                        });
                        this.setTransferDataMap(map);
                        this.setAddedItemIds(addedItemIds);
                    } else {
                        console.log(response);
                        this.setErrorMessage(response.message);
                        this.setShowErrorView({
                            action: () => {
                                this.reset();
                                this.setShowErrorView(undefined);
                                this.setErrorMessage("");
                                this.setup();
                            },
                            message: this.errorMessage,
                        });
                    }
                },
                onFailure: (error) => {
                    this.setIsLoading(false);
                    console.log(error);
                    this.setErrorMessage(
                        "Failed fetching data, please refresh or try again"
                    );
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                },
            }
        );
    }

    // Data Setters
    setLocationOptions(value: LocationModel[]) {
        this.emit(() => {
            this.locationOptions = value;
        });
    }

    setFilterSenderLocationId(value?: number) {
        this.emit(() => {
            this.filterSenderLocationId = value;
        });
    }

    setItemOptions(value: ItemOptions[]) {
        this.emit(() => {
            this.itemOptions = value;
        });
    }

    setSelectedItemId(value?: number) {
        this.emit(() => {
            this.selectedItemId = value;
        });
    }

    setAddedItemIds(value: number[]) {
        this.emit(() => {
            this.addedItemIds = value;
        });
    }

    setSearchQuery(value: string) {
        this.emit(() => {
            this.searchQuery = value;
        });
    }

    setTransferList(value: TransferResponse[]) {
        this.emit(() => {
            this.transferList = value;
        });
    }

    setFilteredTransferList(value: TransferResponse[]) {
        this.emit(() => {
            this.filteredTransferList = value;
        });
    }

    setFormId(value?: number) {
        this.emit(() => {
            this.formId = value;
        });
    }

    setFormCode(value?: string) {
        this.emit(() => {
            this.formCode = value;
        });
    }

    setFormNewSenderLocationId(value?: number) {
        this.emit(() => {
            this.formNewSenderLocationId = value;
        });
    }

    setFormNewReceiverLocationId(value?: number) {
        this.emit(() => {
            this.formNewReceiverLocationId = value;
        });
    }

    setFormPhoneNumber(value?: string) {
        this.emit(() => {
            this.formPhoneNumber = value;
        });
    }

    setTransferDataMap(value: Map<string, AdjustmentTransferModel>) {
        this.emit(() => {
            this.transferDataMap = value;
        });
    }

    handleChangeItemData(
        uniqueId: string,
        field: keyof AdjustmentTransferModel,
        value: number
    ) {
        const updatedMap = new Map(this.transferDataMap);
        const oldData = updatedMap.get(uniqueId);
        if (oldData) {
            updatedMap.set(uniqueId, { ...oldData, [field]: value });
            this.setTransferDataMap(updatedMap);
        }
    }

    handleAddItem(itemId: number) {
        if (!this.addedItemIds.includes(itemId)) {
            const updatedMap = new Map(this.transferDataMap);
            const selectedOption = this.itemOptions.find(
                (option) => option.id === itemId
            );

            const randomNumber = Math.random();
            const uniqueId = `${randomNumber}-${itemId}`;

            updatedMap.set(uniqueId, {
                rowId: randomNumber,
                itemId: itemId,
                uniqueId: uniqueId,
                isFromRevision: false,
                isNew: true,
                bigQty: 0,
                smallQty: 0,
                itemName: selectedOption?.item,
                bigUnit: selectedOption?.bigUnit,
                smallUnit: selectedOption?.smallUnit,
            });
            this.setTransferDataMap(updatedMap);
            this.setAddedItemIds([...this.addedItemIds, itemId]);
            this.setSearchQuery(selectedOption?.item.toLowerCase() ?? "");
        }
    }

    handleSubmit() {
        Swal.fire({
            title: "Konfirmasi",
            text: "Anda yakin ingin melakukan adjustment ini?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Gass",
        }).then((result) => {
            if (result.isConfirmed) {
                if (
                    !this.formId ||
                    !this.formCode ||
                    !this.formNewSenderLocationId ||
                    !this.formNewReceiverLocationId
                ) {
                    this.setShowErrorView({
                        message: "Data tidak lengkap!",
                        action: () => {
                            this.setShowErrorView(undefined);
                        },
                    });
                    return;
                }

                this.setIsLoading(true);

                const items = [...this.transferDataMap.values()].map(
                    (value): AdjustmentTransferModel => ({
                        rowId: value.rowId,
                        itemId: value.itemId,
                        uniqueId: value.uniqueId,
                        isFromRevision: value.isFromRevision,
                        isNew: value.isNew,
                        bigQty: value.bigQty,
                        smallQty: value.smallQty,
                    })
                );

                const submitData: SubmitAdjustmentTransferModel = {
                    formId: this.formId,
                    formCode: this.formCode,
                    formType: "TRANSFER",
                    newLocationFrom: this.formNewSenderLocationId,
                    newLocationTo: this.formNewReceiverLocationId,
                    items,
                };

                ApiService.fetch(Endpoints.adjustmentFormSubmit, {
                    httpMethod: HttpMethod.post,
                    parameters: submitData,
                    onSuccess: (
                        response: BaseResponse<SubmitTransferAdjustmentResponse>
                    ) => {
                        this.setIsLoading(false);
                        if (response.code === 200) {
                            const data =
                                response.data as SubmitTransferAdjustmentResponse;
                            console.log(data);
                            this.showSnackbar = new ShowSnackbarModel({
                                message: "Success Adjust Transfer Form",
                                type: "normal",
                            });
                        } else {
                            console.log(response);
                            this.setErrorMessage(response.message);
                            this.setShowErrorView({
                                action: () => {
                                    this.reset();
                                    this.setup();
                                    this.setShowErrorView(undefined);
                                    this.setErrorMessage("");
                                },
                                message: this.errorMessage,
                            });
                        }
                        this.resetModal();
                        this.setup();
                    },
                    onFailure: (error) => {
                        console.log(error);
                        this.setIsLoading(false);
                        this.setErrorMessage(
                            "Failed fetching data, please refresh or try again"
                        );
                        this.setShowErrorView({
                            action: () => {
                                this.reset();
                                this.setup();
                                this.setShowErrorView(undefined);
                                this.setErrorMessage("");
                            },
                            message: this.errorMessage,
                        });
                    },
                });
            }
        });
    }

    handleVoid(args: {
        transferId: number;
        transferCode: string;
        isVoid: boolean;
    }) {
        Swal.fire({
            title: "Konfirmasi",
            text: "Anda yakin ingin ubah status transfer ini?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Ya",
        }).then((result) => {
            if (result.isConfirmed) {
                ApiService.fetch(Endpoints.adjustmentFormVoidTransfer, {
                    httpMethod: HttpMethod.patch,
                    parameters: {
                        transferId: args.transferId,
                        transferCode: args.transferCode,
                        isVoid: args.isVoid,
                    },
                    onSuccess: (response: BaseResponse<{}>) => {
                        if (response.code === 200) {
                            this.showSnackbar = new ShowSnackbarModel({
                                message:
                                    "Success Ubah Status Void Form Transfer",
                                type: "normal",
                            });
                            this.resetModal();
                            this.setup();
                        } else {
                            console.log(response);
                            this.setErrorMessage(response.message);
                            this.setShowErrorView({
                                action: () => {
                                    this.reset();
                                    this.setup();
                                    this.setShowErrorView(undefined);
                                    this.setErrorMessage("");
                                },
                                message: this.errorMessage,
                            });
                        }
                    },
                    onFailure: (error) => {
                        console.log(error);
                        this.setErrorMessage(error);
                        this.setShowErrorView({
                            action: () => {
                                this.reset();
                                this.setup();
                                this.setShowErrorView(undefined);
                                this.setErrorMessage("");
                            },
                            message: this.errorMessage,
                        });
                    },
                });
            }
        });
    }

    // UI Setters
    setIsLoading(value: boolean) {
        this.emit(() => {
            this.isLoading = value;
        });
    }

    setErrorMessage(value: string) {
        this.emit(() => {
            this.errorMessage = value;
        });
    }

    setShowErrorView(value?: ShowErrorViewModel) {
        this.emit(() => {
            this.showErrorView = value;
        });
    }

    setIsShowModal(value: boolean) {
        this.emit(() => {
            this.isShowModal = value;
        });
    }

    // Reset
    resetModal() {
        this.emit(() => {
            this.isShowModal = false;
            this.formId = undefined;
            this.formCode = undefined;
            this.formNewSenderLocationId = undefined;
            this.formNewReceiverLocationId = undefined;
            this.formPhoneNumber = undefined;
            this.transferDataMap = new Map<string, AdjustmentTransferModel>();
            this.searchQuery = "";
        });
    }

    reset() {
        this.emit(() => {
            this.isLoading = false;
            this.errorMessage = "";
            this.showSnackbar = undefined;
            this.showErrorView = undefined;

            this.locationOptions = [];
            this.filterSenderLocationId = undefined;
            this.transferList = [];
            this.filteredTransferList = [];
            this.searchQuery = "";

            this.formId = undefined;
            this.formCode = undefined;
            this.formNewSenderLocationId = undefined;
            this.formNewReceiverLocationId = undefined;
            this.formPhoneNumber = undefined;
            this.transferDataMap = new Map<string, AdjustmentTransferModel>();
        });
    }
}
